// Implements: F-DSG-002
/**
 * Load the Google families the screen shows, and only those (owner decision 2026-10-18, P5): one
 * `<link rel="stylesheet" data-vx-font="<literal>">` per family, appended to the document head the
 * first time any component shows it, never twice (across re-renders and components: the head itself
 * is the record of what is loaded). A system stack needs nothing. The addresses come from the design
 * model's code table (`googleFontLinks`), never from anything a person typed; the SPA CSP allows the
 * two Google origins (default P5-2). A link stays once added: a family shown once is cached anyway.
 */

import { useEffect } from "react";

/** What `googleFontLinks` builds: a closed family literal and its css2 address. */
export interface FontLink {
  readonly font: string;
  readonly href: string;
}

function isLoaded(font: string): boolean {
  return [...document.head.querySelectorAll<HTMLLinkElement>("link[data-vx-font]")].some(
    (link) => link.dataset.vxFont === font,
  );
}

export function useGoogleFont(links: readonly FontLink[]): void {
  // The effect depends on what is shown, not on a new array per render.
  const wanted = JSON.stringify(links.map((link) => [link.font, link.href]));
  useEffect(() => {
    for (const [font, href] of JSON.parse(wanted) as [string, string][]) {
      if (isLoaded(font)) continue;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.dataset.vxFont = font;
      document.head.append(link);
    }
  }, [wanted]);
}
